import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

const VoteContainer = styled.div`
  padding: 4rem 8rem;
  max-width: 1400px;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  
  @media (max-width: 1200px) {
    padding: 3rem 4rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: center;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 51, 51, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  color: #aaa;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: var(--primary-color);
    margin: 20px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const MainSection = styled.div`
  display: grid;
  grid-template-columns: 8fr 5fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const VotingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const UsernameSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 480px) {
    padding: 1rem;
    gap: 0.75rem;
  }
`;

const MinecraftAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const UsernameInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 480px) {
    padding: 0.65rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const VoteSitesGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const VoteSiteCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease, border-color 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    border-color: var(--primary-color);
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const VoteSiteHeader = styled.div`
  background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const VoteSiteName = styled.h3`
  color: #fff;
  margin: 0;
  font-size: 1.1rem;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const VoteSiteContent = styled.div`
  padding: 1rem;
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const VoteButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-color);
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    background: rgba(221, 0, 0, 0.9);
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const LeaderboardSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const LeaderboardTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #fff;
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
  }
`;

const LeaderboardEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 0.65rem;
    gap: 0.75rem;
  }
`;

const LeaderboardRank = styled.span`
  font-weight: 600;
  color: ${props => {
    if (props.rank === 1) return '#FFD700';
    if (props.rank === 2) return '#C0C0C0';
    if (props.rank === 3) return '#CD7F32';
    return '#fff';
  }};
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const Vote = () => {
  const [username, setUsername] = useState('');
  const [leaderboard] = useState([
    { name: 'Player1', votes: 156 },
    { name: 'Player2', votes: 143 },
    { name: 'Player3', votes: 128 },
    { name: 'Player4', votes: 115 },
    { name: 'Player5', votes: 98 }
  ]);

  const voteSites = [
    {
      name: "Minecraft Server List",
      description: "Vote to receive 10 diamonds and a vote key",
      url: "https://minecraftservers.org/vote/123456"
    },
    {
      name: "Planet Minecraft",
      description: "Vote to receive 5 emeralds and a vote key",
      url: "https://planetminecraft.com/server/lava/vote/"
    },
    {
      name: "Minecraft MP",
      description: "Vote to receive 3 netherite ingots and a vote key",
      url: "https://minecraft-mp.com/server/123456/vote/"
    }
  ];

  return (
    <VoteContainer>
      <Header>
        <PageTitle>VOTE FOR REWARDS</PageTitle>
        <PageSubtitle>
          Support our server by voting daily and earn amazing rewards including vote keys, diamonds, emeralds, and more!
        </PageSubtitle>
      </Header>

      <MainSection>
        <VotingSection>
          <UsernameSection>
            <MinecraftAvatar 
              src={`https://mc-heads.net/avatar/${username || 'steve'}`} 
              alt="Minecraft Avatar" 
            />
            <UsernameInput
              type="text"
              placeholder="Enter your Minecraft username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </UsernameSection>

          <VoteSitesGrid>
            {voteSites.map((site, index) => (
              <VoteSiteCard key={index}>
                <VoteSiteHeader>
                  <VoteSiteName>{site.name}</VoteSiteName>
                  <VoteButton 
                    href={`${site.url}${username ? `?ign=${username}` : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!username}
                  >
                    Vote Now
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </VoteButton>
                </VoteSiteHeader>
                <VoteSiteContent>
                  {site.description}
                </VoteSiteContent>
              </VoteSiteCard>
            ))}
          </VoteSitesGrid>
        </VotingSection>

        <LeaderboardSection>
          <LeaderboardTitle>Top Voters</LeaderboardTitle>
          {leaderboard.map((player, index) => (
            <LeaderboardEntry key={index}>
              <LeaderboardRank rank={index + 1}>#{index + 1}</LeaderboardRank>
              <MinecraftAvatar 
                src={`https://mc-heads.net/avatar/${player.name}`}
                alt={player.name}
                style={{ width: '32px', height: '32px' }}
              />
              <span style={{ flex: 1 }}>{player.name}</span>
              <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>
                {player.votes} votes
              </span>
            </LeaderboardEntry>
          ))}
        </LeaderboardSection>
      </MainSection>
    </VoteContainer>
  );
};

export default Vote;
